import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import {
    IReminderMetadata,
    IReminderPreview,
    IReminderRule,
    IReminderRuleWritable,
    IReminderTestSendResult,
    reminderMetadataSchema,
    reminderPreviewSchema,
    reminderRuleSchema,
    reminderTestSendResultSchema,
} from '../Interfaces/IReminderRule';

export const REMINDER_RULES_QUERY_KEY = ['reminder-rules'] as const;
// Bewusst KEIN Kind von REMINDER_RULES_QUERY_KEY: sonst würde die präfix-basierte
// invalidateQueries(['reminder-rules']) nach jeder Regel-Mutation auch die (langlebige,
// staleTime 5min) Metadaten-Query mit-invalidieren und unnötig neu laden.
export const REMINDER_METADATA_QUERY_KEY = ['reminder-rules-metadata'] as const;

/**
 * customFetcher wirft bei Fehlern `Error: <status> <text> - <body>`. Diese Helper
 * zieht eine menschenlesbare Fehlermeldung aus dem JSON-Body (falls vorhanden) --
 * z. B. `{"detail": [...]}` bei activate/validate oder `{"non_field_errors": [...]}`
 * bei create/update. Liefert `null`, wenn keine Meldung extrahiert werden kann
 * (z. B. bei einem 500er ohne JSON-Body, wie beim PROTECT-Löschkonflikt).
 */
export const extractReminderErrorMessage = (error: unknown): string | null => {
    if (!(error instanceof Error)) {
        return null;
    }
    const braceIndex = error.message.indexOf('{');
    if (braceIndex === -1) {
        return null;
    }
    try {
        const parsed = JSON.parse(error.message.slice(braceIndex)) as Record<string, unknown>;
        const asStrings = (value: unknown): string | null =>
            Array.isArray(value) && value.every((v) => typeof v === 'string') && value.length > 0
                ? value.join(' ')
                : null;

        if (typeof parsed.detail === 'string') {
            return parsed.detail;
        }
        const detailArray = asStrings(parsed.detail);
        if (detailArray) {
            return detailArray;
        }
        const nonFieldErrors = asStrings(parsed.non_field_errors);
        if (nonFieldErrors) {
            return nonFieldErrors;
        }
        for (const value of Object.values(parsed)) {
            const fieldError = asStrings(value);
            if (fieldError) {
                return fieldError;
            }
        }
        return null;
    } catch {
        return null;
    }
};

export const useReminderRules = () => {
    const { data, isLoading, isError, refetch } = useQuery<IReminderRule[], Error>({
        queryKey: REMINDER_RULES_QUERY_KEY,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const data = await customFetcher<unknown>({ endpoint: '/reminder-rules/' });
            return reminderRuleSchema.array().parse(data);
        },
    });
    return { rules: data, isLoadingRules: isLoading, isErrorRules: isError, refetchRules: refetch };
};

export const useReminderMetadata = () => {
    const { data, isLoading, isError } = useQuery<IReminderMetadata, Error>({
        queryKey: REMINDER_METADATA_QUERY_KEY,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const data = await customFetcher<unknown>({ endpoint: '/reminder-rules/metadata/' });
            return reminderMetadataSchema.parse(data);
        },
    });
    return { metadata: data, isLoadingMetadata: isLoading, isErrorMetadata: isError };
};

export const useCreateReminderRule = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createReminderRule, isPending: isCreatingReminderRule } = useMutation<
        IReminderRule,
        Error,
        IReminderRuleWritable
    >({
        mutationFn: async (newRule) => {
            const data = await customFetcher<unknown>({
                endpoint: '/reminder-rules/',
                method: 'POST',
                body: newRule,
            });
            return reminderRuleSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: REMINDER_RULES_QUERY_KEY });
        },
    });
    return { createReminderRule, isCreatingReminderRule };
};

export const useUpdateReminderRule = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateReminderRule, isPending: isUpdatingReminderRule } = useMutation<
        IReminderRule,
        Error,
        { id: number } & Partial<IReminderRuleWritable>
    >({
        mutationFn: async ({ id, ...changes }) => {
            const data = await customFetcher<unknown>({
                endpoint: `/reminder-rules/${id}/`,
                method: 'PATCH',
                body: changes,
            });
            return reminderRuleSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: REMINDER_RULES_QUERY_KEY });
        },
    });
    return { updateReminderRule, isUpdatingReminderRule };
};

export const useDeleteReminderRule = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteReminderRule, isPending: isDeletingReminderRule } = useMutation<void, Error, number>({
        mutationFn: async (id) => {
            await customFetcher({ endpoint: `/reminder-rules/${id}/`, method: 'DELETE' });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: REMINDER_RULES_QUERY_KEY });
        },
    });
    return { deleteReminderRule, isDeletingReminderRule };
};

export const useActivateReminderRule = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: activateReminderRule, isPending: isActivatingReminderRule } = useMutation<
        IReminderRule,
        Error,
        number
    >({
        mutationFn: async (id) => {
            const data = await customFetcher<unknown>({ endpoint: `/reminder-rules/${id}/activate/`, method: 'POST' });
            return reminderRuleSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: REMINDER_RULES_QUERY_KEY });
        },
    });
    return { activateReminderRule, isActivatingReminderRule };
};

export const useDeactivateReminderRule = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deactivateReminderRule, isPending: isDeactivatingReminderRule } = useMutation<
        IReminderRule,
        Error,
        number
    >({
        mutationFn: async (id) => {
            const data = await customFetcher<unknown>({ endpoint: `/reminder-rules/${id}/deactivate/`, method: 'POST' });
            return reminderRuleSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: REMINDER_RULES_QUERY_KEY });
        },
    });
    return { deactivateReminderRule, isDeactivatingReminderRule };
};

/** Lazy/On-Demand-Vorschau (vor Aktivierung oder als eigenständige Aktion). */
export const usePreviewReminderRule = () => {
    const { mutateAsync: previewReminderRule, isPending: isPreviewingReminderRule } = useMutation<
        IReminderPreview,
        Error,
        number
    >({
        mutationFn: async (id) => {
            const data = await customFetcher<unknown>({ endpoint: `/reminder-rules/${id}/preview/` });
            return reminderPreviewSchema.parse(data);
        },
    });
    return { previewReminderRule, isPreviewingReminderRule };
};

export const useTestSendReminderRule = () => {
    const { mutateAsync: testSendReminderRule, isPending: isTestSendingReminderRule } = useMutation<
        IReminderTestSendResult,
        Error,
        { id: number; to?: string }
    >({
        mutationFn: async ({ id, to }) => {
            const data = await customFetcher<unknown>({
                endpoint: `/reminder-rules/${id}/test_send/`,
                method: 'POST',
                body: to ? { to } : {},
            });
            return reminderTestSendResultSchema.parse(data);
        },
    });
    return { testSendReminderRule, isTestSendingReminderRule };
};
